import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import DataGrid from '../ui/DataGrid';
import DataCard from '../ui/DataCard';

/**
 * DashboardTemplatesTab - Vorlagen Tab im Dashboard
 * 
 * Zeigt alle Vorlagen kursübergreifend mit:
 * - Filter nach Kurs
 * - Vorlagen-Info (Name, Fragen, Kurs)
 * - Aktionen (Quiz starten, Bearbeiten)
 */
export default function DashboardTemplatesTab({
  templates,
  courses,
  onStartQuiz
}) {
  const navigate = useNavigate();
  const [filterCourse, setFilterCourse] = useState('all');

  // Get course name by ID
  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course?.name || 'Ohne Kurs';
  };

  // Get course color by ID
  const getCourseColor = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course?.color;
  };

  // Filter templates
  const filteredTemplates = useMemo(() => {
    if (filterCourse === 'all') return templates;
    return templates.filter(t => t.courseId === filterCourse);
  }, [templates, filterCourse]);

  // Sort by last used/created
  const sortedTemplates = useMemo(() => {
    return [...filteredTemplates].sort((a, b) => 
      (b.lastUsed || b.createdAt) - (a.lastUsed || a.createdAt)
    );
  }, [filteredTemplates]);

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Nie';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) return 'Heute';
    if (diff < 172800000) return 'Gestern';
    if (diff < 604800000) return `vor ${Math.floor(diff / 86400000)} Tagen`;
    
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500 dark:text-gray-400">Kurs:</label>
          <select
            value={filterCourse}
            onChange={(e) => setFilterCourse(e.target.value)}
            className="text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">Alle Kurse</option>
            {courses.map(course => (
              <option key={course.id} value={course.id}>
                {course.icon} {course.name}
              </option>
            ))}
          </select>
        </div>
        
        <span className="text-sm text-gray-400">
          {sortedTemplates.length} Vorlage{sortedTemplates.length !== 1 ? 'n' : ''}
        </span>
      </div>

      {/* Templates Grid */}
      <DataGrid
        items={sortedTemplates}
        columns={{ sm: 1, md: 2, lg: 3 }}
        gap="md"
        addCard={{
          label: 'Neue Vorlage erstellen',
          onClick: () => navigate('/template/new')
        }}
        emptyState={{
          title: filterCourse !== 'all' ? 'Keine Vorlagen in diesem Kurs' : 'Noch keine Vorlagen',
          description: 'Erstelle Quizfragen manuell oder mit dem KI-Generator',
          actions: [
            { label: 'Vorlage erstellen', onClick: () => navigate('/template/new') },
            { label: 'Mit KI generieren', onClick: () => navigate('/llm-generator'), variant: 'secondary' }
          ]
        }}
        renderItem={(template) => (
          <DataCard
            key={template.id}
            color={getCourseColor(template.courseId)}
            title={template.name}
            subtitle={getCourseName(template.courseId)}
            stats={[
              { value: template.questions?.length || 0, label: 'Fragen' }
            ]}
            metadata={`Zuletzt: ${formatDate(template.lastUsed || template.createdAt)}`}
            badges={template.tags?.map(tag => ({ label: tag })) || []}
            primaryAction={{
              label: 'Quiz starten',
              onClick: () => onStartQuiz(template.id),
              disabled: !template.questions?.length
            }}
            menuActions={[
              { label: 'Bearbeiten', onClick: () => navigate(`/template/${template.id}`) },
              { label: 'Duplizieren', onClick: () => alert('Duplizieren wird noch implementiert') },
              { label: 'Löschen', onClick: () => alert('Löschen wird noch implementiert'), variant: 'danger' }
            ]}
            onClick={() => navigate(`/template/${template.id}`)}
          />
        )}
      />
    </div>
  );
}

DashboardTemplatesTab.propTypes = {
  templates: PropTypes.array.isRequired,
  courses: PropTypes.array.isRequired,
  onStartQuiz: PropTypes.func.isRequired
};
